import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { generateDramaProject, DramaGenerationError } from '../services/aiDramaService';
import {
  AIProviderConfig,
  GeneratedDramaProject,
  defaultAIProviderConfig,
  formatDramaProjectForExport,
  getLatestDramaProject,
  getStoredAIProviderConfig,
  saveLatestDramaProject,
} from '../services/dramaProjectStore';
import { buildFreeToolPrompt, parsePastedDramaDraft } from '../services/pastedDramaParser';

const CreatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scriptType, setScriptType] = useState('modern');
  const [duration, setDuration] = useState('3min');
  const [creativity, setCreativity] = useState(5);
  const [keywords, setKeywords] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedProject, setGeneratedProject] = useState<GeneratedDramaProject | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const [providerConfig, setProviderConfig] = useState<AIProviderConfig>(defaultAIProviderConfig);
  const [pastedDraft, setPastedDraft] = useState('');

  useEffect(() => {
    setGeneratedProject(getLatestDramaProject());
    setProviderConfig(getStoredAIProviderConfig());
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const templateKeywords = params.get('keywords');
    if (templateKeywords) {
      setKeywords(templateKeywords);
      setStatusMessage('已从资源中心带入这组模板关键词。');
    }
  }, [location.search]);

  const handleGenerate = async () => {
    setErrorMessage('');
    setStatusMessage('');
    setIsGenerating(true);

    try {
      const project = await generateDramaProject(
        {
          keywords: keywords.trim() || '时间胶囊、咖啡馆、反转、真相',
          genre: scriptType,
          duration,
          creativity,
        },
        providerConfig,
      );

      setGeneratedProject(project);
      setStatusMessage('已生成一版可继续拆角色和分镜的短剧。');
    } catch (error) {
      if (error instanceof DramaGenerationError && error.code === 'MISSING_MODEL_CONFIG') {
        setShowConfig(true);
      }

      setErrorMessage(error instanceof Error ? error.message : '生成失败，请稍后再试');
    } finally {
      setIsGenerating(false);
    }
  };

  const applyTemplate = (nextType: string, nextKeywords: string, message: string) => {
    setScriptType(nextType);
    setKeywords(nextKeywords);
    setStatusMessage(message);
  };

  const handleExport = () => {
    if (!generatedProject) {
      return;
    }

    const blob = new Blob([formatDramaProjectForExport(generatedProject)], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${generatedProject.title || 'AI短剧剧本'}.txt`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    setStatusMessage('剧本已经导出到本地文件。');
  };

  const handleCopyFreePrompt = async () => {
    const prompt = buildFreeToolPrompt({
      genre: scriptType,
      duration,
      keywords: keywords.trim(),
    });

    await navigator.clipboard.writeText(prompt);
    setStatusMessage('已复制免费版生成提示词，先去豆包/Kimi/通义出一版，再贴回来整理。');
    setErrorMessage('');
  };

  const handlePasteDraftOrganize = () => {
    setErrorMessage('');

    try {
      const project = parsePastedDramaDraft(pastedDraft, {
        genre: scriptType,
        duration,
        keywords: keywords.trim(),
      });
      saveLatestDramaProject(project);
      setGeneratedProject(project);
      setStatusMessage('已把你粘贴的初稿整理成角色、三集推进和分镜草稿。');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '整理失败，请重新检查粘贴内容。');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-purple-600">首页</Link>
          <span>/</span>
          <span className="text-gray-900">初稿整理</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">短剧初稿整理台</h2>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  剧本类型
                </label>
                <select
                  value={scriptType}
                  onChange={(e) => setScriptType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="modern">现代都市</option>
                  <option value="ancient">古装言情</option>
                  <option value="comedy">喜剧搞笑</option>
                  <option value="suspense">悬疑推理</option>
                  <option value="fantasy">奇幻玄幻</option>
                  <option value="youth">青春校园</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  预计时长
                </label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="1min">1分钟</option>
                  <option value="3min">3分钟</option>
                  <option value="5min">5分钟</option>
                  <option value="10min">10分钟</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  创意程度: {creativity}/10
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={creativity}
                  onChange={(e) => setCreativity(parseInt(e.target.value, 10))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>保守</span>
                  <span>平衡</span>
                  <span>创新</span>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  关键词描述
                </label>
                <textarea
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="描述你想要的内容，比如：时间胶囊、母女和解、旧案反转；咖啡馆、旧友重逢、暧昧拉扯..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 h-24 resize-none"
                />
              </div>

              <div className="mb-6 rounded-xl border border-orange-100 bg-orange-50/70 p-4">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">免费版先出初稿</h3>
                    <p className="text-xs text-gray-600 mt-1">先用豆包、Kimi、通义这类免费工具生成，再贴回来继续拆角色和分镜。</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyFreePrompt}
                    className="shrink-0 px-3 py-2 text-xs bg-white text-orange-700 rounded-lg border border-orange-200 hover:bg-orange-100 transition-colors"
                  >
                    复制提示词
                  </button>
                </div>
                <textarea
                  value={pastedDraft}
                  onChange={(e) => setPastedDraft(e.target.value)}
                  placeholder="把免费版工具生成的标题、人物、三集推进或第1集正文贴到这里。贴整篇也可以。"
                  className="w-full px-3 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 h-36 resize-none bg-white"
                />
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={handlePasteDraftOrganize}
                    className="px-4 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    ✍️ 整理粘贴初稿
                  </button>
                  <button
                    type="button"
                    onClick={() => setPastedDraft('')}
                    className="px-4 py-3 bg-white text-gray-700 font-semibold rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    清空粘贴内容
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowConfig((value) => !value)}
                className="w-full mb-4 px-4 py-2 text-sm bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
              >
                {showConfig ? '收起真实生成设置' : '展开真实生成设置'}
              </button>

              {showConfig && (
                <div className="mb-6 rounded-xl border border-purple-100 bg-purple-50/60 p-4 space-y-4">
                  <div className="rounded-lg bg-white/80 px-3 py-3 text-sm text-purple-700">
                    本页默认每个人使用自己的 API。密钥只保存在你当前浏览器里，不会自动共享给别人。
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">API Key</label>
                    <input
                      type="password"
                      value={providerConfig.apiKey}
                      onChange={(e) => setProviderConfig({ ...providerConfig, apiKey: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="请输入你自己的 API Key"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">接口地址</label>
                    <input
                      type="text"
                      value={providerConfig.baseUrl}
                      onChange={(e) => setProviderConfig({ ...providerConfig, baseUrl: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">模型</label>
                    <input
                      type="text"
                      value={providerConfig.model}
                      onChange={(e) => setProviderConfig({ ...providerConfig, model: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    填好你自己的兼容接口后，就会按你的输入真实生成，不再走站点公用账号。
                  </p>
                </div>
              )}

              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-800 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-purple-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {isGenerating ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    正在真实生成剧本...
                  </div>
                ) : (
                  '🤖 真实生成剧本'
                )}
              </button>

              {statusMessage && (
                <div className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
                  {statusMessage}
                </div>
              )}

              {errorMessage && (
                <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                  {errorMessage}
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-3">快速模板</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => applyTemplate('modern', '咖啡馆、旧友重逢、项目合作、暧昧拉扯', '已套用咖啡馆暧昧系列。')}
                    className="w-full text-left px-3 py-2 text-sm bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
                  >
                    ☕ 咖啡馆暧昧系列
                  </button>
                  <button
                    onClick={() => applyTemplate('suspense', '时间胶囊、母亲旧案、阁楼、现实改写', '已套用时间胶囊反转系列。')}
                    className="w-full text-left px-3 py-2 text-sm bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors"
                  >
                    ⏳ 时间胶囊反转
                  </button>
                  <button
                    onClick={() => applyTemplate('ancient', '古风探案、戏班命案、旧案翻出、雨夜追凶', '已套用古风探案系列。')}
                    className="w-full text-left px-3 py-2 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    🏮 古风探案系列
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            {generatedProject ? (
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                    <div>
                      <p className="text-sm text-purple-600 font-semibold mb-2">{generatedProject.genreLabel} · {generatedProject.durationLabel}</p>
                      <h3 className="text-2xl font-semibold text-gray-900">{generatedProject.title}</h3>
                      <p className="text-gray-600 mt-3">{generatedProject.logline}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleExport}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        导出剧本
                      </button>
                      <button
                        onClick={() => navigator.clipboard.writeText(generatedProject.fullScriptText)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        复制正文
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="p-4 rounded-xl bg-purple-50">
                      <div className="text-sm text-purple-600 font-semibold mb-2">开场钩子</div>
                      <p className="text-gray-700 text-sm">{generatedProject.hook}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-orange-50">
                      <div className="text-sm text-orange-600 font-semibold mb-2">情绪基调</div>
                      <p className="text-gray-700 text-sm">{generatedProject.tone}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-green-50">
                      <div className="text-sm text-green-600 font-semibold mb-2">下一集吊点</div>
                      <p className="text-gray-700 text-sm">{generatedProject.nextEpisodeHook}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {generatedProject.keywords.map((keyword) => (
                      <span key={keyword} className="px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">人物关系</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {generatedProject.characters.map((character) => (
                      <div key={character.id} className="rounded-xl border border-gray-100 p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-semibold text-gray-900">{character.name}</h5>
                          <span className="text-xs text-gray-500">{character.age} · {character.occupation}</span>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{character.personality}</p>
                        <p className="text-sm text-gray-500 mb-3">{character.background}</p>
                        <div className="flex flex-wrap gap-2">
                          {character.relationships.map((relationship) => (
                            <span key={relationship} className="px-2 py-1 rounded-full bg-purple-100 text-purple-700 text-xs">
                              {relationship}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">三集推进</h4>
                  <div className="space-y-4">
                    {generatedProject.episodeBeats.map((beat) => (
                      <div key={beat.episode} className="rounded-xl border border-gray-100 p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-semibold">
                            {beat.episode}
                          </span>
                          <h5 className="font-semibold text-gray-900">{beat.title}</h5>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{beat.summary}</p>
                        <p className="text-sm text-purple-600">吊点：{beat.cliffhanger}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h4 className="text-xl font-semibold text-gray-900 mb-4">第1集完整稿</h4>
                  <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800 bg-gray-50 p-4 rounded-lg">
                    {generatedProject.fullScriptText}
                  </pre>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h4 className="font-medium text-gray-900 mb-3">下一步操作</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={() => navigate('/character-setting')}
                      className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      🎨 生成角色页
                    </button>
                    <button
                      onClick={() => navigate('/storyboard')}
                      className="px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      🎬 生成分镜页
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl">🎭</span>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  先把初稿落成项目
                </h3>
                <p className="text-gray-600 mb-8">
                  这里更像短剧工作台。你可以直接真生成，也可以先拿免费工具出一版，再回来拆角色、推进和分镜。
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="text-purple-600 mb-2">🎯</div>
                    <h4 className="font-medium text-gray-900">先出初稿</h4>
                    <p className="text-sm text-gray-600">支持免费工具先写，也支持你自己的 API 直接生成，不再只剩一条路。</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <div className="text-orange-600 mb-2">⚡</div>
                    <h4 className="font-medium text-gray-900">整理成结构</h4>
                    <p className="text-sm text-gray-600">一篇长稿回来后，会被拆成标题、人物关系、三集推进和正文。</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="text-green-600 mb-2">🎨</div>
                    <h4 className="font-medium text-gray-900">继续往下走</h4>
                    <p className="text-sm text-gray-600">整理好的结果会直接带到角色页和分镜页，不用再从头搬一遍。</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePage;
